/**
 * Browser storage that cannot crash the page (card #11603).
 *
 * When a browser is told to refuse site storage — iOS Safari with "Block All
 * Cookies", Chrome with "Don't allow sites to save data", several in-app
 * browsers — reading the `window.sessionStorage` / `window.localStorage`
 * *property itself* throws `SecurityError` before any key is touched. Every
 * unguarded read in the customer start-up path therefore became an uncaught
 * error, the global handler turned it into the 206 screen, and the menu was
 * never rendered.
 *
 * `SafeWebStorage` wraps one of the two web storages and never throws:
 *
 * - **Storage allowed** (the normal case): reads and writes go straight to the
 *   real storage, under the same keys, so behaviour is unchanged.
 * - **Storage denied**: values are held in an in-memory overlay for the life of
 *   the page, so "already shown" / "already chosen" flags still work for the
 *   whole visit and are simply forgotten on a full reload.
 * - **Storage degrades for one write** (a quota rejection, or Safari private
 *   mode's zero quota): that key falls back to the overlay on its own, and
 *   keys already in the real storage keep being served from it.
 *
 * Every call guards the real operation on its own rather than deciding once
 * whether storage "is available": the property access and the write can fail
 * independently, and a cached verdict would also stop a storage that recovers
 * mid-visit from ever being used again. The overlay is consulted before the
 * real storage on read, which is what makes a key written while degraded
 * readable afterwards; a successful real write drops any overlay copy so the
 * two can never disagree.
 *
 * Deliberately framework-free, not an Angular service: the call sites include
 * `stores.reducer.ts`, whose storage access happens in a module-level pure
 * function that has no injector to read from.
 */
export class SafeWebStorage {
  private readonly overlay = new Map<string, string>();

  constructor(
    private readonly storageName: 'sessionStorage' | 'localStorage',
  ) {}

  getItem(key: string): string | null {
    if (this.overlay.has(key)) {
      return this.overlay.get(key) ?? null;
    }
    try {
      return this.storage().getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @returns true if the real storage now holds the value; false if the
   *          browser refused it and the value is kept in memory for this page
   *          only. Callers that must know whether a value survives a reload
   *          (the customer sign-in handoff) read this; the rest ignore it.
   */
  setItem(key: string, value: string): boolean {
    try {
      this.storage().setItem(key, value);
      // The real storage now holds the value; a stale overlay copy would
      // shadow it on the next read.
      this.overlay.delete(key);
      return true;
    } catch {
      this.overlay.set(key, value);
      return false;
    }
  }

  removeItem(key: string): void {
    this.overlay.delete(key);
    try {
      this.storage().removeItem(key);
    } catch {
      // Best effort: the overlay copy is already gone.
    }
  }

  /**
   * Every key currently readable, overlay and real storage combined. Replaces
   * the `length` / `key(i)` walk callers used to do against the raw storage,
   * which threw at the first property access.
   */
  keys(): string[] {
    const keys = new Set<string>(this.overlay.keys());
    try {
      const storage = this.storage();
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key !== null) {
          keys.add(key);
        }
      }
    } catch {
      // Keep whatever the overlay contributed.
    }
    return Array.from(keys);
  }

  clear(): void {
    this.overlay.clear();
    try {
      this.storage().clear();
    } catch {
      // Best effort.
    }
  }

  /**
   * The raw storage. Throws when the browser refuses it, which is the whole
   * point: every caller above runs this inside its own try/catch.
   */
  private storage(): Storage {
    return window[this.storageName];
  }
}

/** Session-scoped storage for the whole app. Use instead of `sessionStorage`. */
export const safeSessionStorage = new SafeWebStorage('sessionStorage');

/** Origin-scoped storage for the whole app. Use instead of `localStorage`. */
export const safeLocalStorage = new SafeWebStorage('localStorage');
